import { Badge, Box, Button, Card, Divider, getTreeExpandedState, Group, Loader, Modal, Paper, type RenderTreeNodePayload, SegmentedControl, Select, Stack, Tabs, Text, Tree, useTree } from "@mantine/core";
import CodeMirror, { EditorView } from "@uiw/react-codemirror";
import { DataTable, type DataTableColumn } from "mantine-datatable";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { EDITOR_BACKGROUND, EDITOR_STYLE } from "../../common/editor-theme";
import { useInitialHashState, useRegisterShareState } from "../../common/share-state";
import { PANE_INSET, Split } from "../../common/split-panes";
import { UtilityTitle } from "../../common/utility-title";
import { IconChevronRight, IconDatabaseImport, IconPlayerPlay, IconRestore, IconTable, IconTerminal2 } from "../../icons";
import { type Cell, isNull, MAX_ROWS, writeCell } from "./cells";
import { type DatasetId, datasetNamed, DATASETS, datasetScript, isDataset } from "./datasets";
import { editorExtensions } from "./editor";
import { type Engine, failureText, isMode, type ModeId, MODES, openDatabase, type Outcome, populated, type Schema } from "./engine";
import { appended, type LogEntry, type LogLevel, writeLog } from "./logs";
import { EMPTY_SCHEMA, LOAD_UNDONE, loadingMessage, loadWarning, NO_LOG_YET, NO_SCHEMA_YET, NOT_RUN_YET, NOTHING_TO_RUN, ranMessage, readyMessage, RESETTING, RUNNING, schemaFailure, startFailure, startingMessage, summarise, truncatedMessage } from "./messages";

import { oneLine, splitStatements } from "./statements";
import { defaultOpen, type SchemaNode, schemaTree } from "./tree";

// The grid's own stylesheet, imported here so it rides this page's chunk rather than the one every visitor loads.
import "mantine-datatable/styles.css";

export default function Sql() {
  const initialState = useInitialHashState<{ mode?: string; dataset?: string; sql?: string }>();
  const opened: ModeId = isMode(initialState?.mode) ? initialState.mode : "sqlite";
  const [mode, setMode] = useState<ModeId>(opened);

  // Uncontrolled, as in /json: feeding the text back in would replace the document and send the caret to the end.
  const sqlRef = useRef(initialState?.sql ?? "");
  const viewRef = useRef<EditorView | null>(null);
  // The last text this page put in the editor, which is the whole of how it tells its own sample from somebody's own.
  const placed = useRef("");
  // Set by a load, and by a mode switch carrying one over: the next database to come up runs what is in the editor.
  const runWanted = useRef(false);

  // Which dataset the control is offering, and which one this page has written into the editor — the second is null
  // until the Load button has been pressed, and again once somebody's own script has taken that document over.
  const [dataset, setDataset] = useState<DatasetId>(
    isDataset(initialState?.dataset) ? initialState.dataset : DATASETS[0].value,
  );
  const [loaded, setLoaded] = useState<DatasetId | null>(null);
  // Whether the modal is up, which a load puts there when it has something of somebody's own to take away.
  const [asking, setAsking] = useState(false);

  // Bumped by both buttons, so asking for the same database twice is still two databases.
  const [generation, setGeneration] = useState(0);
  const [engine, setEngine] = useState<Engine | null>(null);
  const [starting, setStarting] = useState(true);
  const [running, setRunning] = useState(false);

  const [schemas, setSchemas] = useState<Schema[] | null>(null);
  // What the grid is drawing and how long the statement behind it took; null until one has returned columns.
  const [shown, setShown] = useState<Shown | null>(null);
  // Said in place of a grid when a run had nothing to draw, which a script of writes always does.
  const [note, setNote] = useState<string | null>(null);
  // What the database refused, which is shown whether or not an earlier statement left a grid behind.
  const [problem, setProblem] = useState<string | null>(null);
  const [entries, setEntries] = useState<LogEntry[]>([]);
  const [tab, setTab] = useState<string | null>(RESULTS_TAB);

  // The same catalogue the pane draws, kept where the editor's completion can read it at the moment somebody asks:
  // it is state as well, and an extension rebuilt to carry it would reconfigure the editor after every run.
  const catalogue = useRef<Schema[]>(NO_SCHEMAS);
  useEffect(() => {
    catalogue.current = schemas ?? NO_SCHEMAS;
  }, [schemas]);
  const readCatalogue = useCallback(() => catalogue.current, []);
  const extensions = useMemo(() => editorExtensions(mode, readCatalogue), [mode, readCatalogue]);

  // The database is this session's own and is deliberately not on the link: a table somebody filled here is theirs,
  // where the statements that would fill it again are already in the editor above. What the link does carry is which
  // dataset the control is offering, so a query written against one arrives beside the button that loads it.
  // An empty editor is dropped rather than sent, an absent script being the one the page opens on anyway.
  const syncShareState = useRegisterShareState(() => ({ mode, dataset, sql: sqlRef.current || undefined }));

  const record = useCallback((level: LogLevel, ...texts: string[]) => {
    const at = Date.now();
    setEntries((entries) => appended(entries, texts.map((text) => ({ at, level, text }))));
  }, []);

  // `window.sqlEditor` is how devtools and the Playwright spec read the script; drop it on the way out.
  useEffect(() => () => {
    self.sqlEditor = undefined;
  }, []);

  // Everything in the editor, a statement at a time, against the database it is handed: the one the page is holding
  // when Execute is pressed, and the one just opened when a load is filling it. It takes both rather than reading
  // either from state, and is stable for it — a run whose identity moved would be a run that reopened the database
  // it was being run against, the effect below having it in its dependencies.
  const runScript = useCallback(async (target: Engine, dialect: ModeId) => {
    const statements = splitStatements(sqlRef.current, dialect);
    if (statements.length === 0) {
      setShown(null);
      setProblem(null);
      setNote(NOTHING_TO_RUN);
      return;
    }

    setRunning(true);
    setNote(null);
    setProblem(null);
    const began = performance.now();
    let last: Shown | null = null;
    let done = 0;
    let refused: string | null = null;

    for (const statement of statements) {
      const started = performance.now();
      try {
        const outcome = await target.execute(statement.sql);
        const ms = performance.now() - started;
        done++;
        record("notice", ...outcome.notices);
        record("query", `${oneLine(statement.sql)} — ${summarise(outcome, ms)}`);
        // The grid shows the last statement that answered with columns, which a script of writes never does.
        if (outcome.columns.length > 0) last = { outcome, ms };
      } catch (error) {
        refused = failureText(error);
        record("error", `line ${statement.line}: ${refused}`);
        break;
      }
    }

    setShown(last);
    setProblem(refused);
    setNote(refused || last ? null : ranMessage(done, performance.now() - began));
    setRunning(false);

    // A statement may have rewritten the catalogue, and there is no asking a database whether one did.
    try {
      setSchemas(await target.inspect());
    } catch (error) {
      record("error", schemaFailure(failureText(error)));
    }
  }, [record]);

  // The whole of the loading state: the engine's chunk is fetched, an empty database is opened on it, and the page is
  // handed something to run against. A mode switched, a reset or a dataset loaded starts it again, and the database
  // that was open is closed on the way out whether it was ever handed over or not. Nothing is seeded — a load asks
  // instead for the script it has just written into the editor to be run the moment that database is up, which is how
  // a dataset gets in and how it comes back in the other dialect after a mode switch. The run is handed the engine
  // this effect opened rather than the one in state, which is still the old database on the render it belongs to.
  useEffect(() => {
    let live = true;
    let held: Engine | null = null;

    setEngine(null);
    setStarting(true);
    setSchemas(null);
    setShown(null);
    setNote(null);
    setProblem(null);

    (async () => {
      try {
        const started = await openDatabase(mode);
        held = started;
        if (!live) return;

        setEngine(started);
        setStarting(false);
        record("engine", readyMessage(started.version));

        if (runWanted.current) {
          runWanted.current = false;
          await runScript(started, mode);
          return;
        }

        const read = await started.inspect();
        if (live) setSchemas(read);
      } catch (error) {
        if (!live) return;
        setStarting(false);
        setProblem(startFailure(mode, failureText(error)));
        record("error", startFailure(mode, failureText(error)));
      }
    })();

    return () => {
      live = false;
      void held?.close();
    };
  }, [generation, mode, record, runScript]);

  const handleChange = useCallback((next: string) => {
    sqlRef.current = next;
    syncShareState();
  }, [syncShareState]);

  // Dispatched rather than assigned, so the editor, the ref and the share link all move together.
  const writeSample = useCallback((next: string) => {
    const view = viewRef.current;
    if (!view) return;
    view.dispatch({ changes: { from: 0, to: view.state.doc.length, insert: next } });
    placed.current = next;
  }, []);

  // A dialect changing is nobody asking for a new script, so it only ever rewrites one this page put there itself —
  // a sample being a sample of the dialect and the data it was written for, the rule /javascript applies to its two
  // languages. A load is the other case: it was asked for, so it writes over whatever is there once it has asked.
  const replaceSample = useCallback((next: string) => {
    if (sqlRef.current === placed.current) writeSample(next);
  }, [writeSample]);

  // An untouched editor still holds this page's own script, so a dataset is written out again in the other dialect
  // and run again against the database the switch opens, which is the whole of how the same tables come back. A
  // script somebody has written in is theirs, is left exactly as it is, and is nothing this page would run unasked —
  // and `loaded` goes with it, the editor no longer holding the dataset it names. With no dataset loaded there is
  // nothing to write in the new dialect either, so an untouched editor goes back to empty.
  const handleMode = useCallback((next: ModeId) => {
    const carried = loaded !== null && sqlRef.current === placed.current ? loaded : null;
    replaceSample(carried ? datasetScript(datasetNamed(carried), next) : "");
    runWanted.current = carried !== null;
    setLoaded(carried);
    setMode(next);
  }, [loaded, replaceSample]);

  // A reset is the initial state, and the initial state is empty: what puts data back is the button beside it.
  const handleReset = useCallback(() => {
    record("engine", RESETTING);
    setLoaded(null);
    setGeneration((generation) => generation + 1);
  }, [record]);

  const filled = useMemo(() => populated(schemas ?? NO_SCHEMAS), [schemas]);

  // Three things at once, which is what a load is: the script that builds the dataset goes into the editor, the
  // database underneath it is thrown away for an empty one, and that script is run the moment the empty one is up.
  // Nothing here touches a database directly — the tables arrive through Execute's own path, so the log has a line
  // per CREATE and the grid ends on the query the script closes with, exactly as if somebody had run it themselves.
  const applyLoad = useCallback(() => {
    setAsking(false);
    record("engine", loadingMessage(datasetNamed(dataset).label));
    writeSample(datasetScript(datasetNamed(dataset), mode));
    setLoaded(dataset);
    runWanted.current = true;
    setGeneration((generation) => generation + 1);
  }, [dataset, mode, record, writeSample]);

  // A load is a reset with a script written over the editor and run against what the reset opened, so it stops for
  // anything either of those takes away; a blank editor over an empty database is loaded into without a word.
  const handleLoad = useCallback(() => {
    if (sqlRef.current.trim() === "" && !filled) return applyLoad();
    setAsking(true);
  }, [applyLoad, filled]);

  const handleExecute = useCallback(() => {
    if (!engine || running) return;
    void runScript(engine, mode);
  }, [engine, mode, running, runScript]);

  const busy = starting || running;
  // What the engine is called once it is up, and what it is doing while it is not; a failure is said in the results
  // pane instead, where the statement that could not run would have been answered.
  const status = starting ? startingMessage(mode, loaded && datasetNamed(loaded).label) : engine?.version;

  return (
    <Stack flex={1} className="fill-screen" gap="md">
      <UtilityTitle
        directory="sql"
        control={
          <SegmentedControl
            value={mode}
            onChange={(value) => handleMode(value as ModeId)}
            aria-label="Database"
            disabled={running}
            data={MODES.map((entry) => ({ value: entry.value, label: entry.label }))}
          />
        }
      >
        SQL
      </UtilityTitle>

      <Card withBorder shadow="sm" radius="md">
        <Group align="center" gap="lg" justify="space-between" wrap="nowrap">
          <Group align="center" gap="sm" wrap="nowrap">
            <Button onClick={handleExecute} disabled={busy} leftSection={<IconPlayerPlay size="1rem" />}>
              Execute
            </Button>
            {/* The database is not on the share link, so this is the only thing that can put it back as it opened. */}
            <Button variant="default" onClick={handleReset} disabled={busy} leftSection={<IconRestore size="1rem" />}>
              Reset database
            </Button>

            {/* Stretched rather than given a height, so the rule is exactly as tall as the controls either side. */}
            <Divider orientation="vertical" mx="xs" style={{ alignSelf: "stretch" }} />

            <Select
              w={150}
              aria-label="Example dataset"
              value={dataset}
              onChange={(value) => isDataset(value) && setDataset(value)}
              disabled={busy}
              allowDeselect={false}
              data={DATASETS.map((entry) => ({ value: entry.value, label: entry.label }))}
            />
            <Button
              variant="default"
              onClick={handleLoad}
              disabled={busy}
              leftSection={<IconDatabaseImport size="1rem" />}
            >
              Load
            </Button>
          </Group>

          <Group gap="xs" wrap="nowrap" style={{ minWidth: 0 }}>
            {starting && <Loader size="xs" />}
            {status && <Text size="sm" c="dimmed" truncate="end">{status}</Text>}
          </Group>
        </Group>
      </Card>

      <Split
        direction="row"
        label="Resize the schema pane"
        initial={DEFAULT_SCHEMA_WIDTH}
        floors={SCHEMA_FLOORS}
        second={<SchemaPane schemas={schemas} />}
        first={
          <Split
            direction="column"
            label="Resize the results pane"
            initial={DEFAULT_RESULTS_HEIGHT}
            floors={RESULTS_FLOORS}
            first={
              <Paper withBorder shadow="sm" radius="md" style={{ position: "relative", overflow: "hidden" }}>
                <Box style={{ position: "absolute", top: 0, left: 0, right: 0, bottom: 0 }}>
                  <CodeMirror
                    value={sqlRef.current}
                    // A percentage height only resolves against a definite one, so the wrapper matches the editor.
                    height="100%"
                    style={EDITOR_STYLE}
                    theme="dark"
                    extensions={extensions}
                    onCreateEditor={(view) => {
                      viewRef.current = view;
                      self.sqlEditor = view;
                    }}
                    onChange={handleChange}
                  />
                </Box>
              </Paper>
            }
            second={
              <Paper withBorder shadow="sm" radius="md" style={{ position: "relative", overflow: "hidden" }}>
                <Box style={PANE_INSET}>
                  <Tabs
                    value={tab}
                    onChange={setTab}
                    // keepMounted false, or the grid lays out its rows while the log is the tab being read.
                    keepMounted={false}
                    style={{ display: "flex", flexDirection: "column", flex: 1, minHeight: 0 }}
                  >
                    <Tabs.List>
                      <Tabs.Tab value={RESULTS_TAB} leftSection={<IconTable size="0.9rem" />}>Results</Tabs.Tab>
                      <Tabs.Tab value={LOGS_TAB} leftSection={<IconTerminal2 size="0.9rem" />}>
                        Logs{entries.length > 0 && ` (${entries.length.toLocaleString()})`}
                      </Tabs.Tab>
                    </Tabs.List>

                    <Tabs.Panel value={RESULTS_TAB} style={PANEL_FILL}>
                      <Results shown={shown} note={note} problem={problem} running={running} />
                    </Tabs.Panel>

                    <Tabs.Panel value={LOGS_TAB} style={PANEL_FILL}>
                      <Logs entries={entries} />
                    </Tabs.Panel>
                  </Tabs>
                </Box>
              </Paper>
            }
          />
        }
      />

      {/* The one thing standing between the Load button and a database nobody can get back, so it names both losses. */}
      <Modal opened={asking} onClose={() => setAsking(false)} title="Load over what is here?" centered>
        <Stack gap="lg">
          <Text size="sm">{loadWarning(datasetNamed(dataset).label, filled)} {LOAD_UNDONE}</Text>
          <Group justify="flex-end" gap="sm">
            <Button variant="default" onClick={() => setAsking(false)}>Cancel</Button>
            <Button color="red" onClick={applyLoad}>Reset and load</Button>
          </Group>
        </Stack>
      </Modal>
    </Stack>
  );
}

// The last statement that answered with columns, and how long that statement took.
interface Shown {
  outcome: Outcome;
  ms: number;
}

function Results({ shown, note, problem, running }: ResultsProps) {
  const rows = shown?.outcome.rows ?? EMPTY_ROWS;
  const columns = shown?.outcome.columns ?? EMPTY_COLUMNS;

  // Keyed by position rather than by name, because `SELECT 1 AS a, 2 AS a` is two columns and one of them would
  // otherwise be unreachable — a grid that quietly drops a column is worse than one that repeats a heading.
  const records = useMemo(
    () =>
      rows.slice(0, MAX_ROWS).map((cells, index) => {
        const record: Record<string, Cell> = { [ROW_KEY]: index };
        cells.forEach((cell, at) => {
          record[String(at)] = cell;
        });
        return record;
      }),
    [rows],
  );

  const grid = useMemo<DataTableColumn<Record<string, Cell>>[]>(
    () =>
      columns.map((name, at) => ({
        accessor: String(at),
        title: name,
        // The grid's own drag handles, which is what makes a column of long text something worth widening.
        resizable: true,
        ellipsis: true,
        render: (record) => <CellText value={record[String(at)]} />,
      })),
    [columns],
  );

  // A refusal is its own line rather than the grid's absence, since the grid may be an earlier statement's answer
  // and saying nothing about the one that failed would leave a stale table reading as the result.
  const message = running ? RUNNING : note ?? (problem ? null : NOT_RUN_YET);

  return (
    <>
      {problem && <Text size="xs" c="red" px="sm" py={6} style={{ ...BAR, whiteSpace: "pre-wrap" }}>{problem}</Text>}

      {shown
        ? (
          <>
            <Group px="sm" py={6} gap="sm" wrap="nowrap" style={BAR}>
              <Text size="xs" c="dimmed" truncate="end">{summarise(shown.outcome, shown.ms)}</Text>
              {rows.length > MAX_ROWS && (
                <Text size="xs" c="yellow" style={{ flex: "none" }}>{truncatedMessage(MAX_ROWS, rows.length)}</Text>
              )}
            </Group>

            <Box style={{ flex: 1, minHeight: 0 }}>
              <DataTable
                records={records}
                columns={grid}
                idAccessor={ROW_KEY}
                height="100%"
                withColumnBorders
                striped
                highlightOnHover
                noRecordsText="The statement returned no rows."
                verticalSpacing={4}
                horizontalSpacing="sm"
              />
            </Box>
          </>
        )
        : (
          <Box p="sm" style={{ flex: 1, overflow: "auto" }}>
            {message && <Text size="sm" c="dimmed">{message}</Text>}
          </Box>
        )}
    </>
  );
}

interface ResultsProps {
  shown: Shown | null;
  note: string | null;
  problem: string | null;
  running: boolean;
}

// A span rather than Mantine's default paragraph, since the cell's own ellipsis has nothing to clip a block with.
function CellText({ value }: { value: Cell }) {
  const text = writeCell(value);
  return (
    <Text component="span" size="xs" ff="monospace" c={isNull(value) ? "dimmed" : undefined} title={text}>
      {text}
    </Text>
  );
}

// One text node rather than ten thousand rows of elements, for the reason /python's output panel is one: this is a
// transcript to be read down, and the browser lays it out in a single pass.
function Logs({ entries }: { entries: LogEntry[] }) {
  const text = useMemo(() => writeLog(entries), [entries]);
  const scroller = useRef<HTMLDivElement>(null);

  // Follows the newest line unless somebody has scrolled up, which a busy script would otherwise drag them off.
  useEffect(() => {
    const box = scroller.current;
    if (!box) return;
    const away = box.scrollHeight - box.scrollTop - box.clientHeight;
    if (away < FOLLOW_SLACK) box.scrollTop = box.scrollHeight;
  }, [text]);

  return (
    <Box ref={scroller} style={{ flex: 1, minHeight: 0, overflow: "auto", backgroundColor: EDITOR_BACKGROUND }}>
      {/* A log is the live region a screen reader reads out as it fills, which is what this is. */}
      <Text
        component="pre"
        role="log"
        aria-label="Database log"
        ff="monospace"
        fz="xs"
        p="sm"
        m={0}
        c={entries.length === 0 ? "dimmed" : undefined}
        style={{ whiteSpace: "pre-wrap", overflowWrap: "anywhere" }}
      >
        {entries.length === 0 ? NO_LOG_YET : text}
      </Text>
    </Box>
  );
}

function SchemaPane({ schemas }: { schemas: Schema[] | null }) {
  const tree = useTree();
  // Identity is what Tree reads a change from, so the same catalogue must not become a new set of nodes on a render.
  const nodes = useMemo(() => schemaTree(schemas ?? []), [schemas]);

  // Opened once per database rather than on every refresh, or a branch somebody closed would spring back after the
  // next statement. The Tree's own effect runs before this one, since it is the child of the two.
  const settled = useRef(false);
  useEffect(() => {
    if (nodes.length === 0) {
      settled.current = false;
      return;
    }
    if (settled.current) return;
    settled.current = true;
    tree.setExpandedState(getTreeExpandedState(nodes, defaultOpen(nodes)));
  }, [nodes, tree]);

  return (
    <Paper withBorder shadow="sm" radius="md" style={{ position: "relative", overflow: "hidden" }}>
      <Box style={PANE_INSET}>
        <Box px="sm" py="xs" style={{ borderBottom: "1px solid var(--mantine-color-default-border)" }}>
          <Text size="sm" fw={500}>Schema</Text>
        </Box>
        <Box p="xs" style={{ flex: 1, overflow: "auto" }}>
          {nodes.length === 0
            ? <Text size="sm" c="dimmed" px="xs">{schemas === null ? NO_SCHEMA_YET : EMPTY_SCHEMA}</Text>
            : <Tree data={nodes} tree={tree} aria-label="Database schema" levelOffset="md" renderNode={renderRow} />}
        </Box>
      </Box>
    </Paper>
  );
}

function renderRow({ node, expanded, hasChildren, elementProps }: RenderTreeNodePayload) {
  const { name, kind, detail, marks, block } = (node as SchemaNode).row;
  const { style, ...rest } = elementProps;

  // A definition is a paragraph of SQL rather than a row, so it is laid out as one and never truncated.
  if (block) {
    return (
      <Box {...rest} style={{ ...style, cursor: "default" }}>
        <Text size="xs" ff="monospace" c="bright" style={{ whiteSpace: "pre-wrap" }}>{name}</Text>
      </Box>
    );
  }

  return (
    <Group {...rest} style={{ ...style, cursor: hasChildren ? "pointer" : "default" }} gap="xs" wrap="nowrap">
      <IconChevronRight
        size="0.8rem"
        // A leaf keeps the space the chevron would take, so every row at a level starts at the same column.
        style={{
          flex: "0 0 auto",
          visibility: hasChildren ? undefined : "hidden",
          transform: expanded ? "rotate(90deg)" : undefined,
        }}
      />
      <Text size="xs" ff="monospace" style={{ flex: "0 0 auto" }}>{name}</Text>
      {kind && <Text size="xs" c="dimmed" style={{ flex: "0 0 auto" }}>{kind}</Text>}
      {marks?.map((mark) => (
        <Badge key={mark} size="xs" variant="light" color="gray" tt="none" style={{ flex: "0 0 auto" }}>{mark}</Badge>
      ))}
      {/* The detail is what somebody opened the branch to read, so it is the one thing in the brightest ink. */}
      {detail && (
        <Text size="xs" ff="monospace" c="bright" flex={1} miw={0} truncate="end" title={detail}>{detail}</Text>
      )}
    </Group>
  );
}

// A line across the top of the results pane, which the summary and a refusal both take a turn at.
const BAR = { flex: "none", borderBottom: "1px solid var(--mantine-color-default-border)" };

const RESULTS_TAB = "results";
const LOGS_TAB = "logs";

// A panel is the column the grid or the log fills, and it claims what the tab strip above it leaves.
const PANEL_FILL = { flex: 1, minHeight: 0, display: "flex", flexDirection: "column" as const };

// Where the two grips start, and the floors the drag is clamped to, which global.css spells again as min sizes.
const DEFAULT_SCHEMA_WIDTH = 340;
const DEFAULT_RESULTS_HEIGHT = 260;
const SCHEMA_FLOORS = { first: 240, second: 192 };
const RESULTS_FLOORS = { first: 144, second: 128 };

// Close enough to the bottom to count as reading the newest line rather than as having scrolled away from it.
const FOLLOW_SLACK = 24;

// The record key the grid identifies a row by, which is a position and never a column of the answer.
const ROW_KEY = "__row";

// The catalogue before a database has answered with one, which is one array so nothing is rebuilt off a fresh empty.
const NO_SCHEMAS: Schema[] = [];

const EMPTY_ROWS: Cell[][] = [];
const EMPTY_COLUMNS: string[] = [];

declare global {
  var sqlEditor: EditorView | undefined;
}
